import { useCallback, useEffect, useRef, useState } from "react";
import { fetchSnapshot, REFRESH_MS } from "./api";
import type { Snapshot } from "./types";
import { money, pct, num, utcTime, ago } from "./format";
import type { TfWindow } from "./perf";
import { WINDOW_LABEL } from "./perf";
import InstrumentView from "./components/InstrumentView";
import InstrumentGrid from "./components/InstrumentGrid";
import OverviewPerf from "./components/OverviewPerf";
import ActivityLog from "./components/ActivityLog";
import DocsView from "./components/DocsView";
import HermesUpdates from "./components/HermesUpdates";
import TalkToHermes from "./components/TalkToHermes";

const TABS = ["Overview", "Instruments", "Activity", "Hermes Updates", "Talk to Hermes", "Documents"] as const;
type Tab = (typeof TABS)[number];

const WINDOWS: TfWindow[] = ["1Y", "2Y", "ALL"];

export default function App() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("Overview");
  const [ticker, setTicker] = useState<string | null>(null);
  const [tfWindow, setTfWindow] = useState<TfWindow>("1Y");
  const [, setTick] = useState(0); // re-render so "x ago" stays fresh
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const load = useCallback(async () => {
    try {
      setSnap(await fetchSnapshot());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    const clock = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(id); clearInterval(clock); };
  }, [load]);

  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    const last = TABS.length - 1;
    let next = i;
    if (e.key === "ArrowRight") next = i === last ? 0 : i + 1;
    else if (e.key === "ArrowLeft") next = i === 0 ? last : i - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    else return;
    e.preventDefault();
    setTab(TABS[next]);
    tabRefs.current[next]?.focus();
  };

  const accounts = snap?.accounts ?? [];
  const live = accounts.filter((a) => a.status === "live").length;
  const testing = accounts.filter((a) => a.status === "testing").length;
  const selected = accounts.find((a) => a.account_id === ticker) ?? accounts[0];

  return (
    <>
      <a href="#main" className="skip-link">Skip to content</a>
      <header className="topbar">
        <div className="brand">
          <span className="logo" aria-hidden="true">◆</span>
          <h1>QuantFi</h1>
          <span className="tagline">live paper-trading</span>
        </div>
        <div className="status" role="status" aria-live="polite">
          {error ? (
            <span className="err">⚠ {error}</span>
          ) : snap ? (
            <span>
              <span className="live-dot" aria-hidden="true">●</span>
              Updated {utcTime(snap.generated_at)} · {ago(snap.generated_at)} ·{" "}
              {accounts.length} strateg{accounts.length === 1 ? "y" : "ies"} ({live} live, {testing} testing)
            </span>
          ) : (
            <span>Loading…</span>
          )}
          <button className="refresh" onClick={load} aria-label="Refresh now">↻</button>
        </div>
      </header>

      <nav className="tabs" aria-label="Sections">
        <div role="tablist" aria-label="Dashboard sections">
          {TABS.map((t, i) => (
            <button
              key={t} role="tab" id={`tab-${t}`}
              aria-selected={tab === t} aria-controls={`panel-${t}`}
              tabIndex={tab === t ? 0 : -1}
              ref={(el) => (tabRefs.current[i] = el)}
              className={tab === t ? "tab active" : "tab"}
              onClick={() => setTab(t)} onKeyDown={(e) => onTabKey(e, i)}
            >
              {t}
            </button>
          ))}
        </div>
      </nav>

      <main id="main">
        {snap?.empty && (
          <p className="notice">No snapshot yet. Run the local loop: <code>python3 -m live.loop</code>.</p>
        )}

        {tab === "Overview" && (
          <section role="tabpanel" id="panel-Overview" aria-labelledby="tab-Overview">
            <div className="tf-filter">
              <span className="tf-filter-label" id="tf-filter-label">Timeframe</span>
              <div role="radiogroup" aria-labelledby="tf-filter-label" className="tf-seg">
                {WINDOWS.map((w) => (
                  <button
                    key={w}
                    type="button"
                    role="radio"
                    aria-checked={tfWindow === w}
                    className={tfWindow === w ? "tf-opt on" : "tf-opt"}
                    onClick={() => setTfWindow(w)}
                  >
                    {WINDOW_LABEL[w]}
                  </button>
                ))}
              </div>
            </div>
            <h2>Performance <span className="muted small">— over {WINDOW_LABEL[tfWindow]}</span></h2>
            <OverviewPerf accounts={accounts} window={tfWindow} onOpen={(id) => { setTicker(id); setTab("Instruments"); }} />
            <h2>Live instruments <span className="muted small">— all {accounts.length} trading now</span></h2>
            <InstrumentGrid accounts={accounts} onOpen={(id) => { setTicker(id); setTab("Instruments"); }} />
            <h2>Strategies at a glance</h2>
            <div className="table-wrap" role="region" aria-label="All strategies summary" tabIndex={0}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Instrument</th><th scope="col">Status</th>
                    <th scope="col">Trades</th><th scope="col">Win rate</th>
                    <th scope="col">PF</th><th scope="col">Sharpe</th><th scope="col">P&amp;L</th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.account_id}>
                      <th scope="row">{a.instrument} <span className="muted">· {a.tf}</span></th>
                      <td>{a.status}</td>
                      <td>{num(a.metrics?.n_trades, 0)}</td>
                      <td>{pct(a.metrics?.win_rate)}</td>
                      <td>{num(a.metrics?.profit_factor)}</td>
                      <td>{num(a.metrics?.sharpe)}</td>
                      <td>{money(a.metrics?.total_pnl)}</td>
                      <td>
                        <button className="linklike" onClick={() => { setTicker(a.account_id); setTab("Instruments"); }}>
                          View →
                        </button>
                      </td>
                    </tr>
                  ))}
                  {accounts.length === 0 && (
                    <tr><td colSpan={8} className="muted">No strategies registered.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "Instruments" && (
          <section role="tabpanel" id="panel-Instruments" aria-labelledby="tab-Instruments">
            <div className="ticker-bar" role="tablist" aria-label="Instruments">
              {accounts.map((a) => {
                const on = (selected?.account_id ?? "") === a.account_id;
                return (
                  <button
                    key={a.account_id} role="tab" aria-selected={on}
                    className={on ? "ticker on" : "ticker"}
                    onClick={() => setTicker(a.account_id)}
                  >
                    {a.instrument}<span className="ticker-tf">{a.tf}</span>
                  </button>
                );
              })}
            </div>
            {selected ? <InstrumentView acct={selected} window={tfWindow} /> : <p className="muted">No instruments yet.</p>}
          </section>
        )}

        {tab === "Activity" && (
          <section role="tabpanel" id="panel-Activity" aria-labelledby="tab-Activity">
            <h2>Live activity log</h2>
            <p className="muted">Every scan, signal, entry (▲) and exit (✕) across all instruments — newest first, times in UTC.</p>
            <ActivityLog events={snap?.events ?? []} showInstrument />
          </section>
        )}

        {tab === "Hermes Updates" && (
          <section role="tabpanel" id="panel-Hermes Updates" aria-labelledby="tab-Hermes Updates">
            <h2>Hermes — autonomous research feed</h2>
            <p className="muted">
              Hermes runs continuously in a Docker container (GLM-4.6 via z.ai), focused on
              XAUUSD / gold price-action scalping (ICT &amp; SMC). It posts what it is learning,
              the strategies it is developing, its walkforward backtests and its decisions — newest first.
            </p>
            <HermesUpdates updates={snap?.hermes_updates ?? []} />
          </section>
        )}

        {tab === "Talk to Hermes" && (
          <section role="tabpanel" id="panel-Talk to Hermes" aria-labelledby="tab-Talk to Hermes">
            <h2>Talk to Hermes</h2>
            <TalkToHermes chat={snap?.hermes_chat ?? []} />
          </section>
        )}

        {tab === "Documents" && (
          <section role="tabpanel" id="panel-Documents" aria-labelledby="tab-Documents">
            <h2>Research &amp; reports</h2>
            <DocsView docs={snap?.docs ?? []} reports={snap?.reports ?? []} />
          </section>
        )}
      </main>

      <footer className="foot">
        <p>
          Paper-trading only — no real capital. All times UTC. See Documents for the methodology
          and the random-sign-null discipline.
        </p>
      </footer>
    </>
  );
}
